import { hashString } from '@/helpers/ssrHandoff/hashState';
import { getSsrPiniaSnapshot } from '@/helpers/ssrHandoff/piniaSnapshot';
import { getRouteFromRouter } from '@/helpers/ssrHandoff/routerContext';
import type {
  PiniaStateChange,
  SsrFingerprint,
  SsrHandoffMismatch,
} from '@/helpers/ssrHandoff/types';
import { captureError } from '@smg-real-estate/browser-rum';
import type { Pinia } from 'pinia';
import type { Router } from 'vue-router';

const RUM_ENABLED_STAGES = new Set(['test', 'prod']);
const MAX_PINIA_DIFFS = 25;

const isRumEnabled = (): boolean =>
  typeof window !== 'undefined' && RUM_ENABLED_STAGES.has(import.meta.env.VUE_APP_STAGE);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const valuesAreEqual = (expected: unknown, actual: unknown): boolean =>
  JSON.stringify(expected) === JSON.stringify(actual);

const collectPiniaStateChanges = (
  expected: Record<string, unknown> | undefined,
  actual: Record<string, unknown>,
  path = '',
  changes: PiniaStateChange[] = [],
): PiniaStateChange[] => {
  if (changes.length >= MAX_PINIA_DIFFS) {
    return changes;
  }

  const expectedRecord = expected ?? {};
  const keys = new Set([...Object.keys(expectedRecord), ...Object.keys(actual)]);

  keys.forEach((key) => {
    if (changes.length >= MAX_PINIA_DIFFS) {
      return;
    }

    const currentPath = path ? `${path}.${key}` : key;
    const hasExpected = Object.hasOwn(expectedRecord, key);
    const hasActual = Object.hasOwn(actual, key);
    const expectedValue = expectedRecord[key];
    const actualValue = actual[key];

    if (!hasExpected && hasActual) {
      changes.push({ path: currentPath, kind: 'added' });
      return;
    }

    if (hasExpected && !hasActual) {
      changes.push({ path: currentPath, kind: 'removed' });
      return;
    }

    if (isPlainObject(expectedValue) && isPlainObject(actualValue)) {
      collectPiniaStateChanges(expectedValue, actualValue, currentPath, changes);
      return;
    }

    if (!valuesAreEqual(expectedValue, actualValue)) {
      changes.push({ path: currentPath, kind: 'changed' });
    }
  });

  return changes;
};

export const collectSsrHandoffMismatches = ({
  fingerprint,
  pinia,
  router,
  locale,
  ssrPiniaState,
}: {
  fingerprint: SsrFingerprint;
  pinia: Pinia;
  router: Router;
  locale: string;
  ssrPiniaState?: Record<string, unknown>;
}): SsrHandoffMismatch[] => {
  const mismatches: SsrHandoffMismatch[] = [];
  const clientLocale = locale;
  const clientRoute = getRouteFromRouter(router);
  const clientPiniaState = pinia.state.value as Record<string, unknown>;
  const clientPiniaHash = hashString(JSON.stringify(clientPiniaState));

  if (fingerprint.locale !== clientLocale) {
    mismatches.push({
      field: 'locale',
      expected: fingerprint.locale,
      actual: clientLocale,
      diff: `${fingerprint.locale} → ${clientLocale}`,
    });
  }

  if (fingerprint.route !== clientRoute) {
    mismatches.push({
      field: 'route',
      expected: fingerprint.route,
      actual: clientRoute,
      diff: `${fingerprint.route} → ${clientRoute}`,
    });
  }

  if (fingerprint.piniaHash !== clientPiniaHash) {
    mismatches.push({
      field: 'pinia',
      expected: fingerprint.piniaHash,
      actual: clientPiniaHash,
      diff: collectPiniaStateChanges(ssrPiniaState, clientPiniaState),
    });
  }

  return mismatches;
};

const formatMismatchDiff = ({ field, diff }: SsrHandoffMismatch): string => {
  if (typeof diff === 'string') {
    return `${field}: ${diff}`;
  }

  const changes = diff.map(({ path, kind }) => `${path} (${kind})`).join(', ');

  return `${field}: ${changes}`;
};

const buildSsrHandoffMismatchSummary = (mismatches: SsrHandoffMismatch[]): string =>
  mismatches.map(formatMismatchDiff).join('; ');

const buildSsrHandoffMismatchDiff = (mismatches: SsrHandoffMismatch[]) =>
  Object.fromEntries(mismatches.map(({ field, diff }) => [field, diff]));

const buildSsrHandoffMismatchError = (mismatches: SsrHandoffMismatch[]): Error => {
  const error = new Error(`SSR handoff mismatch: ${buildSsrHandoffMismatchSummary(mismatches)}`);
  error.name = 'SsrHandoffMismatch';

  return error;
};

export const reportSsrHandoffMismatch = (mismatches: SsrHandoffMismatch[]): void => {
  if (mismatches.length === 0) {
    return;
  }

  const error = buildSsrHandoffMismatchError(mismatches);
  const diff = buildSsrHandoffMismatchDiff(mismatches);

  if (!isRumEnabled()) {
    console.error('[ssr-handoff]', error.message, {
      diff,
      url: window.location.pathname,
    });
    return;
  }

  captureError(error, {
    source: 'ssr-handoff',
    ssr_handoff_diff: diff,
    ssr_handoff_mismatches: mismatches.map(({ field, expected, actual, diff: fieldDiff }) => ({
      field,
      expected,
      actual,
      diff: fieldDiff,
    })),
    ssr_handoff_locale: mismatches.find(({ field }) => field === 'locale'),
    ssr_handoff_route: mismatches.find(({ field }) => field === 'route'),
    ssr_handoff_pinia: mismatches.find(({ field }) => field === 'pinia'),
    url: window.location.pathname,
  });
};

export const verifySsrHandoff = ({
  pinia,
  router,
  locale,
}: {
  pinia: Pinia;
  router: Router;
  locale: string;
}): SsrHandoffMismatch[] => {
  const fingerprint = window.__SSR_FINGERPRINT__;

  if (!fingerprint) {
    return [];
  }

  const mismatches = collectSsrHandoffMismatches({
    fingerprint,
    pinia,
    router,
    locale,
    ssrPiniaState: getSsrPiniaSnapshot(),
  });

  reportSsrHandoffMismatch(mismatches);

  return mismatches;
};
